import {
  ArrowDownLeftIcon,
  ArrowDownRightIcon,
  ArrowUpRightIcon,
  BasketIcon,
  CreditCardIcon,
  EyeIcon,
  EyeSlashIcon,
  GearSixIcon,
  InfoIcon,
  UserPlusIcon,
  PiggyBankIcon,
  PlusIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react'
import {
  type PlainDate,
  type PoolBreakdown,
  formatMoney,
  percentOf,
} from '@paybae/shared'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import { AvisoIngresoPendiente } from '@/components/AvisoIngresoPendiente'
import { AvisoJustificar } from '@/components/AvisoJustificar'
import { CampanaAvisos } from '@/components/CampanaAvisos'
import { HojaInvitacion } from '@/components/Invitacion'
import { CommitmentDonut } from '@/components/CommitmentDonut'
import { BloqueTinta, FilaAgendaEditorial, type Tinte } from '@/components/Editorial'
import { DetalleDinero } from '@/components/DetalleDinero'
import { ProgressRing } from '@/components/Donut'
import { ErrorState, Skeleton } from '@/components/Estado'
import {
  type SeleccionMapa,
  type TenenciaDinero,
} from '@/components/MapaDelDinero'
import { Money } from '@/components/Money'
import { Preparacion } from '@/components/Preparacion'
import { PersonAvatar, PersonDot } from '@/components/PersonAvatar'
import type { CashflowResponse } from '@/lib/api'
import { cn } from '@/lib/cn'
import { haptic } from '@/lib/haptics'
import {
  useCards,
  useCashflow,
  useDashboard,
  useGoals,
  useMeses,
  useRecurring,
} from '@/lib/queries'
import { useSession } from '@/lib/session'

/**
 * Variante C del inicio — «Editorial».
 *
 * TESIS
 *
 * La pantalla se compone como la portada de una revista, no como una retícula
 * de tarjetas iguales: **una pieza manda y el resto se subordina**. El bloque
 * de apertura funde la cifra del disponible con el mapa del dinero —titular y
 * fotografía de portada— y todo lo que sigue baja de tamaño en escalones
 * claros. Lo que separa las secciones son filetes finos y un rótulo numerado,
 * no cajas: menos contenedores, más composición.
 *
 * POR QUÉ ASÍ
 *
 * Las dos direcciones anteriores encapsulaban cada dato en su propia tarjeta.
 * Con quince bloques encajonados nada pesa más que lo demás, y el ojo tiene que
 * decidir por su cuenta qué leer primero. Una jerarquía tipográfica ancha —de
 * 44 px a 10 px— hace ese trabajo antes de que el usuario piense: el titular se
 * lee de pie en el metro, el cuerpo se lee sentado.
 *
 * RIESGO ASUMIDO
 *
 * Una composición editorial se rompe con un nombre largo o con el texto grande
 * del sistema. Por eso todo el tipo del titular usa `clamp`, cada fila tiene su
 * columna mínima declarada y ninguna cifra se trunca: se reduce el cuerpo, no
 * el dato.
 *
 * LO QUE NO SE TOCA
 *
 * El interior del mapa del dinero y la cápsula de navegación. Aquí solo cambia
 * el marco del mapa: pierde la tarjeta y gana el halo y el aire de la apertura.
 */
export function Dashboard() {
  const navigate = useNavigate()
  const { profile, membership } = useSession()

  const [scope, setScope] = useState<Scope>(
    membership.household.kind === 'PERSONAL' ? 'personal' : 'combined',
  )
  const [activeMemberId, setActiveMemberId] = useState(membership.id)
  /** Ocultar montos: el gesto de mirar el saldo en público sin enseñarlo. */
  const [oculto, setOculto] = useState(false)

  const { data, isPending, isError, error, refetch } = useDashboard()
  const { data: cards } = useCards()
  const { data: goals } = useGoals()
  const { data: flujo } = useCashflow(6)
  // Fijos y compras a meses alimentan «Lo que viene»: sus obligaciones se
  // funden con las nóminas y los cortes en una sola línea de tiempo.
  const fijos = useRecurring()
  const compras = useMeses()

  const [detalle, setDetalle] = useState<SeleccionMapa | null>(null)
  const [invitando, setInvitando] = useState(false)

  const pool: PoolBreakdown | null = useMemo(() => {
    if (!data) return null
    if (scope === 'joint') return data.availability.joint ?? null
    if (scope === 'personal') return data.availability.perMember[activeMemberId] ?? null
    return combinar(data.availability)
  }, [data, scope, activeMemberId])

  const tenencias: TenenciaDinero[] = useMemo(() => {
    if (!data) return []
    const personas = data.members.map((m) => {
      const bolsa = data.availability.perMember[m.memberId]
      return {
        holderId: m.memberId,
        nombre: (m.nickname || m.displayName).split(' ')[0]!,
        colorToken: m.personColorToken,
        libreCents: Math.max(0, bolsa?.freeCents ?? 0),
        ahorradoCents: m.savedCents,
        gastadoCents: m.spentCents,
        deudaCents: bolsa?.cardDebtCents ?? 0,
      }
    })

    if (!data.availability.joint || !data.joint) return personas
    return [
      ...personas,
      {
        holderId: null,
        nombre: 'Fondo común',
        colorToken: null,
        libreCents: Math.max(0, data.availability.joint.freeCents),
        ahorradoCents: data.joint.savedCents,
        gastadoCents: data.joint.spentCents,
        deudaCents: data.availability.joint.cardDebtCents,
      },
    ]
  }, [data])

  /**
   * La agenda: nóminas que llegan y estados de cuenta que vencen, en una sola
   * lista ordenada por fecha. Se acota a treinta días: más allá deja de ser
   * «lo que viene» y pasa a ser un calendario, que no es lo que esta sección
   * contesta.
   *
   * Cada renglón lleva el tinte de lo que ES, con el mismo reparto de toda la
   * app: mantequilla lo que entra, durazno los fijos que salen, lavanda lo que
   * pasa por tarjeta. Así la lista se lee por color antes de leer un nombre
   * —«dos amarillos este mes» ya dice cuántas veces cobras—. Lo urgente no
   * cambia el tinte: se marca con el icono, para que el color no diga dos cosas.
   */
  const agenda = useMemo(() => {
    if (!data) return []
    const items: Array<{
      key: string
      fecha: PlainDate
      tinte: Tinte
      icono?: React.ReactNode
      titulo: string
      detalle: string
      derecha: React.ReactNode
    }> = []

    for (const ingreso of data.upcomingIncomes ?? []) {
      if (ingreso.daysUntil > 30) continue
      const quien = data.members.find((m) => m.memberId === ingreso.memberId)
      // El nombre de la fuente y no solo «Nómina de Daniel»: quien cobra sueldo
      // y además factura vería dos renglones idénticos el mismo día.
      const nombre = (quien?.nickname || quien?.displayName || '').split(' ')[0]
      items.push({
        key: `ingreso-${ingreso.incomeSourceId}-${ingreso.date}`,
        fecha: ingreso.date,
        tinte: 'butter',
        titulo: nombre ? `${ingreso.name} de ${nombre}` : ingreso.name,
        detalle:
          ingreso.daysUntil === 0
            ? 'Hoy'
            : ingreso.daysUntil === 1
              ? 'Mañana'
              : `En ${ingreso.daysUntil} días`,
        derecha: <Money cents={ingreso.amountCents} tone="income" showSign />,
      })
    }

    for (const c of cards ?? []) {
      const est = c.closedStatement
      if (!est || est.balanceDueCents <= 0) continue
      const urgente =
        est.countdown.urgency === 'OVERDUE' || est.countdown.urgency === 'DUE_SOON'
      items.push({
        key: `tarjeta-${c.id}`,
        fecha: est.dueDate,
        tinte: 'lavender',
        icono: urgente ? (
          <WarningCircleIcon size={14} weight="fill" className="text-expense-text" />
        ) : undefined,
        titulo: `${c.name}${c.last4 ? ` ·· ${c.last4}` : ''}`,
        detalle: vencimiento(est.countdown.calendarDaysUntilDue),
        derecha: <Money cents={est.balanceDueCents} tone={urgente ? 'expense' : 'soft'} />,
      })
    }

    /*
     * Los gastos fijos pendientes, en la MISMA lista.
     *
     * Vivían en su propio bloque, y las compras a meses en un tercero, así que
     * las obligaciones del mes estaban repartidas en cuatro sitios y ninguno
     * decía en qué orden iban a ocurrir. Fundidas aquí, la fecha ordena todo y
     * la lista se puede leer en diagonal: es la respuesta entera a «¿qué me va a
     * caer encima?» en una sola pasada.
     */
    for (const f of fijos.data ?? []) {
      if (!f.active || f.postedThisPeriod) continue
      if (f.nextDueOn > data.periodEnd) continue
      items.push({
        key: `fijo-${f.id}`,
        fecha: f.nextDueOn,
        // Durazno aunque vaya a tarjeta: es un fijo, y los fijos salen. El
        // detalle de abajo ya dice por dónde.
        tinte: 'peach',
        titulo: f.name,
        detalle: f.paymentSource === 'CREDIT_CARD' ? 'Fijo · va a tarjeta' : 'Fijo · sale de tu cuenta',
        derecha: <Money cents={f.amountCents} />,
      })
    }

    // Y las mensualidades a meses, con su vencimiento real: el día en que de
    // verdad sale el dinero, no el del corte que las factura.
    for (const p of compras.data?.plans ?? []) {
      if (p.status !== 'ACTIVE' || !p.nextInstallment) continue
      items.push({
        key: `meses-${p.id}`,
        fecha: p.nextInstallment.dueDate,
        tinte: 'lavender',
        // El número de ESTA mensualidad, no cuántas faltan. Decía
        // `remainingCount`: con 5 de 12 cobradas salía «iPhone · 7 de 12», que
        // se lee como «la séptima», mientras Compras a meses decía 5/12 y la
        // que viene es la sexta. Dos pantallas, dos números para lo mismo.
        titulo: `${p.name} · ${p.nextInstallment.number} de ${p.months}`,
        detalle: `A meses · corte ${dia(p.nextInstallment.cutoffDate)}`,
        derecha: <Money cents={p.nextInstallment.amountCents} />,
      })
    }

    return items.sort((a, b) => a.fecha.localeCompare(b.fecha))
  }, [data, cards, fijos.data, compras.data])

  /**
   * La numeración de las secciones.
   *
   * Se calcula a partir de las que de verdad se dibujan, no se escribe a mano:
   * con números fijos, esconder una sección vacía dejaba un salto —05, 07— que
   * se lee como si faltara algo. Aquí una sección que no aparece simplemente no
   * gasta número.
   */
  const num = useMemo(() => {
    const visibles = [
      'dinero',
      ...(agenda.length > 0 ? ['viene'] : []),
      ...((goals ?? []).length > 0 ? ['metas'] : []),
    ]
    return (clave: string): string => String(visibles.indexOf(clave) + 1).padStart(2, '0')
  }, [agenda.length, goals])

  /**
   * Quién tiene vales, cuando hay más de uno.
   *
   * Los vales no se prestan entre integrantes: el saldo de cada tarjeta es de
   * quien la lleva. Un total del hogar contesta «cuánto hay» pero no «puedo
   * pagar yo», que es la pregunta de quien está en la caja del súper.
   */
  const valesPorPersona = useMemo(() => {
    if (!data) return null
    const conVales = data.members
      .map((m) => ({
        nombre: (m.nickname || m.displayName).split(' ')[0],
        cents: data.availability.perMember[m.memberId]?.voucherCents ?? 0,
      }))
      .filter((x) => x.cents > 0)
    if (conVales.length < 2) return null
    return conVales
      .map((x) => `${x.nombre} ${formatMoney(x.cents, { hideZeroCents: true })}`)
      .join(' · ')
  }, [data])

  if (isPending) return <Cargando />
  if (isError || !data) {
    return (
      <Pagina>
        <ErrorState
          titulo="No pudimos cargar tu tablero"
          mensaje={error instanceof Error ? error.message : 'Inténtalo de nuevo.'}
          onReintentar={() => void refetch()}
        />
      </Pagina>
    )
  }

  const efectivo = pool ?? vacio()
  // Solo quien administra puede crear invitaciones; el servidor responde 403 al
  // resto, así que enseñarles el botón sería ofrecer una puerta cerrada.
  const esAdmin = membership.role === 'ADMIN'
  const miembros = data.members
  const activo = miembros.find((m) => m.memberId === activeMemberId)
  const sinFondoComun = data.availability.joint === null

  // «Hoy» en la etiqueta no es un adorno: el titular dejó de incluir la nómina
  // que no ha caído, y decirlo en el rótulo evita tener que deducirlo de la
  // línea de abajo.
  const etiqueta =
    scope === 'combined'
      ? miembros.length > 1
        ? 'Disponible hoy entre los dos'
        : 'Disponible hoy'
      : scope === 'joint'
        ? 'Disponible hoy del fondo común'
        : `Disponible hoy de ${(activo?.displayName ?? profile.displayName).split(' ')[0]}`




  /**
   * La nómina que falta por caer dentro del periodo.
   *
   * El monto lo manda el motor —`expectedIncomeCents`, que ya excluye lo
   * confirmado y los días de pago vencidos—; de `upcomingIncomes` solo se toma
   * la **fecha**, para poder decir «el 31 de ago» en vez de un genérico «este
   * periodo». Se filtra por alcance porque el titular también lo está: en la
   * vista de una persona no cabe anunciar la quincena de la otra.
   */
  const nominaPorCaer = (() => {
    const cents = efectivo.expectedIncomeCents
    if (cents <= 0) return null

    const dentroDelPeriodo = (data.upcomingIncomes ?? []).filter(
      (i) =>
        i.kind === 'FIXED' &&
        i.date <= data.periodEnd &&
        (scope !== 'personal' || i.memberId === activeMemberId),
    )
    return {
      cents,
      // Una sola fecha se puede nombrar; varias se resumen, porque «el 15 y el
      // 31 y el 20» ocupa más de lo que aclara.
      fecha: dentroDelPeriodo.length === 1 ? dentroDelPeriodo[0]!.date : null,
    }
  })()

  const { entero, centavos } = partirMonto(efectivo.freeCents)
  const variacion = variacionDeGasto(flujo)

  return (
    <Pagina oculto={oculto}>
      {/* ── Cabecera: el membrete ───────────────────────────────────────────
          Un filete debajo y la línea de fecha en versalitas monoespaciadas.
          Es lo que convierte el encabezado en la cabecera de una publicación y
          no en otra barra de app; además la fecha del hogar es un dato que
          antes solo vivía escondido dentro del periodo. */}
      {/* ── El bloque de tinta ──────────────────────────────────────────────
          Cabecera, fecha, titular y acciones dejan de ser cuatro piezas sobre
          papel y pasan a ser **un solo bloque a sangre**. Es el gesto que
          sostiene toda la dirección: un bloque plano que llega a los tres
          bordes convierte la cifra en titular de portada, mientras que la misma
          cifra sobre papel se lee como el saldo de un cajero.
          A sangre y plano: sin degradado y sin sombra interior. */}
      <BloqueTinta>
      <header className="flex items-center gap-3 pb-4">
        <button
          type="button"
          onClick={() => {
            haptic('light')
            navigate('/ajustes')
          }}
          className="flex min-h-[44px] min-w-0 flex-1 items-center gap-3 text-left active:opacity-70"
          aria-label="Abrir ajustes"
        >
          <PersonAvatar name={profile.displayName} token={membership.personColorToken} size="md" />
          <span className="min-w-0 flex-1">
            {/* `soft` y no `faint`: el gris tenue da 3.1:1 sobre la tinta, por
                debajo del mínimo para texto. El tenue queda para los iconos. */}
            <span className="ed-versalita block text-on-navy-soft">{saludo()}</span>
            <span className="flex items-center gap-1 truncate font-display text-[16px] font-bold tracking-[-0.03em] text-on-navy">
              <span className="truncate">{profile.displayName}</span>
              <GearSixIcon size={13} className="shrink-0 text-on-navy-faint" />
            </span>
          </span>
        </button>
        {/* Invitar, junto a la campana.
            Solo lo ve quien administra: a los demás les saldría un botón que
            responde 403. Y no navega a ninguna parte —abre la hoja aquí
            mismo—: invitar es una gestión de treinta segundos, y mandar a otra
            pantalla obliga a volver. */}
        {esAdmin ? (
          <button
            type="button"
            onClick={() => {
              haptic('light')
              setInvitando(true)
            }}
            aria-label="Invitar a alguien a tu hogar"
            className="flex size-11 shrink-0 items-center justify-center rounded-full text-on-navy active:opacity-60"
          >
            <UserPlusIcon size={19} />
          </button>
        ) : null}

        {/* La campanita trae su propia consulta: el punto sale de `sinVer` de la
            bandeja y se apaga solo al leerla. Antes era un literal que estaba
            siempre encendido y un `onClick` que solo vibraba. */}
        <CampanaAvisos sobreTinta />
      </header>

      {/* El periodo, en chip de mantequilla.
          Es el único acento de marca de la pantalla y se gasta aquí: dice de
          qué ventana habla todo lo que viene debajo, que es el dato que más se
          consulta y el que antes vivía en una línea de texto gris. */}
      <p className="ed-fechado inline-flex rounded-pill bg-brand px-2.5 py-1 !text-on-brand">
        {fechado(data.today, data.periodStart, data.periodEnd)}
      </p>

      {/* ── Apertura: el titular ─────────────────────────────────────────── */}
      <section className="relative pb-1 pt-5">
        <div className="relative">
          {miembros.length > 1 ? (
            <Alcance
              valor={scope}
              onCambio={setScope}
              sinFondoComun={sinFondoComun}
              className="mb-6"
            />
          ) : null}

          {scope === 'personal' && miembros.length > 1 ? (
            <div className="mb-5 flex flex-wrap gap-2">
              {miembros.map((m) => {
                const seleccionado = m.memberId === activeMemberId
                return (
                  <button
                    key={m.memberId}
                    type="button"
                    onClick={() => {
                      haptic('selection')
                      setActiveMemberId(m.memberId)
                    }}
                    aria-pressed={seleccionado}
                    className={cn(
                      'flex min-h-[44px] items-center gap-2 rounded-pill py-1.5 pl-1.5 pr-3.5 transition-colors',
                      seleccionado
                        ? 'bg-brand text-on-brand'
                        : 'bg-transparent text-on-navy-soft ring-1 ring-inset ring-white/20',
                    )}
                  >
                    <PersonAvatar name={m.displayName} token={m.personColorToken} size="sm" />
                    <span className="max-w-[7rem] truncate text-[13px] font-semibold">
                      {m.displayName.split(' ')[0]}
                    </span>
                  </button>
                )
              })}
            </div>
          ) : null}

          <div className="flex items-center gap-2">
            <p className="ed-versalita text-on-navy-soft">{etiqueta}</p>
            {/* El ojo apaga todos los montos de la pantalla a la vez, no solo
                el titular: enseñar el saldo tapado mientras el desglose sigue
                a la vista no oculta nada. */}
            <button
              type="button"
              onClick={() => {
                haptic('light')
                setOculto((v) => !v)
              }}
              aria-pressed={oculto}
              aria-label={oculto ? 'Mostrar montos' : 'Ocultar montos'}
              className="-my-2 -ml-1 flex size-11 items-center justify-center text-on-navy-faint active:opacity-60"
            >
              {oculto ? <EyeSlashIcon size={17} /> : <EyeIcon size={17} />}
            </button>
          </div>

          {/* El rojo se reserva para el sobregiro de verdad: que hoy el efectivo
              no cubra los compromisos del periodo es lo normal el día 2, y
              teñirlo entrenaría a ignorar el color el mes que sí falte dinero.
              La cifra que decide es la proyectada.

              Y el número **se puede tocar**: abre el desglose de cómo se calcula.
              Antes ese desglose solo se alcanzaba desde el mapa del dinero, así
              que al mudar el mapa a Informes se habría quedado sin puerta. Es
              mejor sitio del que tenía: la pregunta «¿de dónde sale este número?»
              se hace mirando el número, no una gráfica de reparto. */}
          <button
            type="button"
            onClick={() => {
              haptic('light')
              setDetalle({
                estado: 'libre',
                holderId: scope === 'joint' ? null : activeMemberId,
              })
            }}
            aria-label="Ver cómo se calcula tu disponible"
            className="-mx-1 flex items-baseline gap-1.5 rounded-card px-1 text-left active:opacity-70"
          >
            <span
              data-money
              className={cn('ed-titular mt-1.5', efectivo.projectedFreeCents < 0 && 'text-expense')}
            >
              {entero}
              {/* Los centavos en cuerpo pequeño sobre la misma línea de base: el
                  peso visual va a los pesos, que es lo que se compara. */}
              <span className="ed-centavos">{centavos}</span>
            </span>
            <InfoIcon size={15} className="shrink-0 text-on-navy-faint" />
          </button>

          {/* La nómina que falta por caer, fuera del titular.
              Antes vivía dentro: quien tenía $20,900 en el banco y dos quincenas
              de $23,500 por delante leía «Disponible $67,900». El número grande
              es un dato —lo que hay ahora—, y lo que va a llegar es una segunda
              frase con su fecha y su total. Así se puede planear el mes sin que
              la app prometa un dinero que todavía no existe. */}
          {nominaPorCaer ? (
            <p className="mt-2.5 flex items-start gap-1.5 text-[12.5px] leading-snug text-on-navy-soft">
              <ArrowDownLeftIcon size={14} className="mt-0.5 shrink-0 text-income" />
              <span>
                <span data-money className="font-semibold text-income">
                  +{formatMoney(nominaPorCaer.cents, { hideZeroCents: true })}
                </span>{' '}
                {nominaPorCaer.fecha
                  ? `por caer el ${dia(nominaPorCaer.fecha)}`
                  : 'por caer este periodo'}
                {' · llegarías a '}
                <span data-money className="font-semibold text-on-navy">
                  {formatMoney(efectivo.projectedFreeCents, { hideZeroCents: true })}
                </span>
              </span>
            </p>
          ) : null}

          {/* Aquí vivía una línea que restaba la deuda total de tarjetas del
              disponible proyectado, y era una resta sin sentido: la deuda de una
              compra a dieciocho meses está repartida en dieciocho cortes, y
              quitarla de la proyección de UN periodo mezcla dos unidades
              distintas. Decía «al pagar los $9,138 te quedarían $35,261» cuando
              nadie va a pagar esos $9,138 este mes.
              Lo que sí toca pagar en el periodo ya lo descuenta el motor dentro
              del titular (`committedCardsCents`), y el total de la deuda se
              reporta abajo, en «En tarjetas», con su plazo. */}

          {/* Los vales, pegados al saldo.
              No se suman al disponible —solo compran despensa, y sumarlos
              mentiría sobre lo que se puede pagar—, pero son dinero real y
              tenerlos tres pantallas más abajo obligaba a bajar a buscarlos
              justo cuando sirven: parado en la caja del súper decidiendo si
              pagas con tarjeta o con vales. Van en su propia línea, en cuerpo
              menor y con el «aparte» dicho, para que se lean como una segunda
              bolsa y nunca como parte de la cifra de arriba. */}
          {efectivo.voucherCents > 0 ? (
            <p className="mt-2.5 flex items-start gap-1.5 text-[12.5px] leading-snug text-on-navy-soft">
              <BasketIcon size={14} className="mt-0.5 shrink-0 text-brand" />
              <span>
                <span data-money className="font-semibold text-on-navy">
                  {formatMoney(efectivo.voucherCents, { hideZeroCents: true })}
                </span>{' '}
                en vales, aparte
                {/* El reparto por persona, aquí y no en un bloque propio.
                    Los vales no se prestan entre integrantes, así que «en casa
                    hay $3,800» no contesta «puedo pagar YO la despensa de hoy»,
                    que es la pregunta de quien está en la caja del súper. Vivía
                    seis pantallas más abajo; pegado al titular llega a tiempo. */}
                {valesPorPersona ? (
                  <span className="block text-on-navy-soft">{valesPorPersona}</span>
                ) : null}
              </span>
            </p>
          ) : null}

          {variacion ? (
            <p
              className={cn(
                'ed-pildora mt-3',
                variacion.baja ? 'ed-pildora--bien' : 'ed-pildora--sobre-tinta',
              )}
            >
              {variacion.baja ? (
                <ArrowDownRightIcon size={13} weight="bold" />
              ) : (
                <ArrowUpRightIcon size={13} weight="bold" />
              )}
              {variacion.pct}% {variacion.baja ? 'menos' : 'más'} de gasto que en{' '}
              {variacion.mes}
            </p>
          ) : null}

          {/* ── Acciones ────────────────────────────────────────────────
              Van entre el titular y el anillo, no después. Debajo del anillo
              caían por fuera del primer pliegue en cuanto el hogar tiene dos
              personas —el selector de alcance añade una fila— y una acción que
              hay que ir a buscar deja de usarse. Aquí quedan a un pulgar del
              número, que es de donde se decide gastar.

              Pastillas de tinte pleno, no cuadrados de tinte suave: sobre el
              bloque de tinta un tinte claro desaparece. Cada una lleva el color
              de lo que hace —mantequilla entra, durazno sale, menta aparta— y
              así se reconocen por color antes de leer la etiqueta. */}
          <div className="mt-6 grid grid-cols-4 gap-2.5">
            <AccionPortada
              Icon={PlusIcon}
              label="Gasto"
              tinte="bg-tint-lavender-solid text-on-brand"
              onClick={() => navigate('/capturar')}
            />
            <AccionPortada
              Icon={PiggyBankIcon}
              label="Ahorrar"
              tinte="bg-tint-mint-solid text-on-brand"
              onClick={() => navigate('/ahorro')}
            />
            <AccionPortada
              Icon={CreditCardIcon}
              label="Pagar"
              tinte="bg-tint-peach-solid text-on-brand"
              onClick={() => navigate('/tarjetas')}
            />
            {/* Ingreso, no «Invitar».
                Las cuatro acciones son las cuatro formas en que se mueve el
                dinero: sale, entra, se aparta y se paga la tarjeta. Invitar es
                algo que se hace una vez y vive en Ajustes; ocupaba un cuarto de
                la fila más visible de la app para una acción que casi nadie
                repite, mientras registrar un ingreso no tenía puerta en
                ninguna parte. */}
            <AccionPortada
              Icon={ArrowDownLeftIcon}
              label="Ingreso"
              tinte="bg-tint-butter-solid text-on-brand"
              onClick={() => navigate('/ingreso')}
            />
          </div>

        </div>
      </section>
      </BloqueTinta>

      {/* Los avisos, en el papel y no dentro del bloque.
          Son interrupciones: piden una acción con fecha —una quincena sin
          confirmar deja el disponible mintiendo— y tienen que leerse como algo
          que irrumpe, no como parte del encabezado. Dentro del bloque de tinta
          se habrían leído como decoración del titular. Van justo debajo, que
          sigue siendo lo primero del papel, y se callan solos cuando no hay
          nada que hacer. */}
      <div className="ed-avisos-arriba flex flex-col gap-3 empty:hidden">
        <AvisoIngresoPendiente />
        <AvisoJustificar />
      </div>

      {/* La rampa de entrada va ANTES de las cifras.
          Un tablero con la mitad de los números en cero no se lee como «te
          falta configurar algo», se lee como «esta app no sirve». Esto lo dice
          con todas sus letras y lleva a cada pantalla; cuando ya no falta
          nada, desaparece solo. */}
      <Preparacion />

      {/*
        TRES BLOQUES SE MUDARON DE AQUÍ, Y NO ES UN RECORTE: ES EL ARREGLO.

        El inicio llegó a tener trece secciones y **tres donas** que decían cosas
        parecidas con colores distintos —el mapa del dinero, el gasto por
        categoría y la dona de compromiso—. Con eso, la pantalla dejó de contestar
        «¿cuánto puedo gastar hoy?» y se convirtió en un tablero de analista que
        hay que estudiar.

        Cada una se fue a donde se usa, no a la basura:

        - «En qué gastaste» → pestaña **Movimientos**, pegada a los movimientos
          que la explican. Una dona de categorías sin la lista al lado obliga a
          cambiar de pantalla para saber qué hay dentro de cada tramo.
        - «Tu mes en tres cifras» y el **mapa del dinero** → **Informes**, que es
          la pantalla donde se analiza. El mapa además era inútil en un hogar de
          una persona: una dona de un solo tramo al 100%.
        - «Despensa y tarjetas» → los vales ya viven pegados al titular, que es
          donde se consultan (parado en la caja del súper), y la deuda de tarjeta
          vive en la pestaña Tarjetas, con su desglose.

        Queda una sola dona, y es la que responde la pregunta del inicio.
      */}

      <Seccion numero={num('dinero')} titulo="Dónde está tu dinero">
        <CommitmentDonut breakdown={efectivo} />
      </Seccion>

      {/*
        «Lo que viene» — una sola línea de tiempo.
        Antes eran cuatro bloques: pagos fijos, próximos movimientos, compras a
        meses y cortes de tarjeta. Cuatro listas, cada una con su criterio de
        orden, y ninguna capaz de contestar lo único que se le pregunta a esta
        parte de la pantalla: en qué orden van a pasar las cosas. Ahora es una
        lista ordenada por fecha donde cada renglón dice qué es y de dónde sale.
      */}
      {agenda.length > 0 ? (
        <Seccion
          numero={num('viene')}
          titulo="Lo que viene"
          accion={{ texto: 'Ver fijos', onClick: () => navigate('/fijos') }}
        >
          <ul className="flex flex-col gap-1.5">
            {agenda.map((item) => {
              const [, mes, d] = item.fecha.split('-')
              return (
                <li key={item.key}>
                  <FilaAgendaEditorial
                    dia={String(Number(d))}
                    mes={MESES[Number(mes) - 1]}
                    tinte={item.tinte}
                    icono={item.icono}
                    titulo={item.titulo}
                    detalle={item.detalle}
                    monto={<span className="text-[14px]">{item.derecha}</span>}
                  />
                </li>
              )
            })}
          </ul>
        </Seccion>
      ) : null}

      {(goals ?? []).length > 0 ? (
        <Seccion
          numero={num('metas')}
          titulo="Metas de ahorro"
          accion={{ texto: 'Ver todas', onClick: () => navigate('/ahorro') }}
        >
          <ul className="ed-filas">
            {(goals ?? []).slice(0, 3).map((goal) => {
              // Un fondo sin monto objetivo no tiene porcentaje: `percentOf`
              // con cero devuelve 0 y el anillo queda vacío, que es lo correcto
              // cuando no hay final que alcanzar.
              const pct = percentOf(goal.currentAmountCents, goal.targetAmountCents ?? 0)
              const alcanzada = pct >= 100
              const owner = goal.ownerMemberId
                ? miembros.find((m) => m.memberId === goal.ownerMemberId)
                : null
              return (
                <li key={goal.id} className="flex items-center gap-3 py-3">
                  <ProgressRing
                    percent={pct}
                    color={alcanzada ? 'var(--income)' : 'var(--primary)'}
                  >
                    <span data-money className="text-[11px] font-bold text-ink">
                      {Math.round(pct)}
                    </span>
                  </ProgressRing>
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-1.5 truncate text-[14px] font-semibold text-ink">
                      {owner ? <PersonDot token={owner.personColorToken} /> : null}
                      <span className="truncate">{goal.name}</span>
                    </p>
                    <p data-money className="truncate text-[12px] text-ink-soft">
                      {formatMoney(goal.currentAmountCents, { hideZeroCents: true })}
                      {goal.targetAmountCents === null
                        ? ' guardados'
                        : ` de ${formatMoney(goal.targetAmountCents, { hideZeroCents: true })}`}
                    </p>
                  </div>
                </li>
              )
            })}
          </ul>
        </Seccion>
      ) : null}

      {/* Colofón: cierra la publicación y recuerda en qué moneda está todo. */}
      <p className="ed-fechado mt-8 border-t border-[var(--regla)] pt-4">
        PayBae · {data.currency} · {data.householdKind === 'PERSONAL' ? 'Personal' : data.householdKind === 'COUPLE' ? 'Pareja' : 'Familia'}
      </p>

      <HojaInvitacion abierta={invitando} onCerrar={() => setInvitando(false)} />

      <DetalleDinero
        seleccion={detalle}
        nombreTitular={
          tenencias.find((t) => t.holderId === detalle?.holderId)?.nombre ?? 'Fondo común'
        }
        bolsa={
          detalle === null
            ? null
            : detalle.holderId === null
              ? data.availability.joint
              : (data.availability.perMember[detalle.holderId] ?? null)
        }
        periodoDesde={data.periodStart}
        periodoHasta={data.periodEnd}
        onCerrar={() => setDetalle(null)}
      />
    </Pagina>
  )
}

/* ── Lienzo ─────────────────────────────────────────────────────────────── */

/**
 * El papel.
 *
 * Marfil cálido en claro y tinta azul-noche en oscuro. Es el cambio de fondo
 * que hace que la pantalla no se lea como otra app de banco: las apps
 * financieras viven en gris frío o en negro, y el papel las saca de ahí sin
 * inventar una paleta nueva —la tinta, el índigo, la menta y el coral son los
 * de PayBae.
 *
 * El papel y sus fichas viven en `tokens.css` y valen para toda la app: un
 * fondo que solo se aplica a una pantalla no es un diseño, es un parche. Aquí
 * quedan solo las que son de esta pantalla —el halo, la trama de puntos y el
 * filete que sustituye a la tarjeta— y la sombra, que sobre papel tiene que ser
 * un filete y no una nube.
 */
function Pagina({ children, oculto = false }: { children: React.ReactNode; oculto?: boolean }) {
  return (
    <div
      data-variante="editorial"
      data-oculto={oculto ? 'true' : undefined}
      className="ed-pagina min-h-dvh"
    >
      <style>{CSS_EDITORIAL}</style>
      <div className="px-safe mx-auto max-w-lg pt-safe">{children}</div>
    </div>
  )
}

const CSS_EDITORIAL = `
[data-variante='editorial'] {
  /* Papel marfil: cálido, apenas fuera del blanco. Sobre él la tinta fría de
     PayBae se lee como periódico, no como pantalla. */
  --papel: #faf8f4;
  --regla: rgb(15 23 41 / 0.14);

  /* Las fichas de superficie y de borde ya vienen del tema: el papel dejó de
     ser de esta pantalla y pasó a ser el de toda la app. Aquí solo se ajusta la
     sombra, que sobre papel tiene que ser un filete y no una nube. */
  --shadow-sm: 0 1px 0 rgb(15 23 41 / 0.05);

  background: var(--papel);
  color: var(--ink);
  /* El zócalo de navegación deja 6.5rem de relleno en el Shell; sin esto el
     papel se cortaría antes de la cápsula y aparecería una franja gris. */
  padding-bottom: calc(6.5rem + env(safe-area-inset-bottom));
  margin-bottom: calc(-6.5rem - env(safe-area-inset-bottom));
  /* El bloque de tinta se sale por los lados con márgenes negativos.
     overflow-x:clip lo recorta sin crear un contenedor de scroll ni afectar a
     las hojas de posición fija. */
  overflow-x: clip;
}

[data-theme='dark'] [data-variante='editorial'] {
  /* Azul noche, nunca negro puro: mantiene la familia de tono del marino de la
     marca y evita el arrastre del negro absoluto en OLED al hacer scroll. */
  --papel: #090f1e;
  --regla: rgb(255 255 255 / 0.14);

  --shadow-sm: 0 1px 0 rgb(0 0 0 / 0.4);
}

/* Ocultar montos: un solo interruptor apaga todas las cifras de la pantalla,
   incluidas las del mapa y las de las hojas. Se desenfoca en vez de sustituir
   por puntos para que la composición no dé un salto al alternar. */
[data-oculto='true'] [data-money] {
  /* El desenfoque se mide en em y no en píxeles: así tapa igual de bien el
     titular de 52 px que un renglón de 13, en vez de dejar legible la grande. */
  filter: blur(0.3em);
  user-select: none;
  transition: filter 160ms ease;
}

/* El titular. Tracking muy cerrado y altura de línea por debajo de 1: es lo que
   convierte una cifra en un titular y no en un dato grande. */
.ed-titular {
  font-family: var(--font-display);
  /* El tope de 3.25rem no es estético: es lo que hace que un saldo de ocho
     cifras siga cabiendo en una línea de 375 px con cifras tabulares. */
  font-size: clamp(2.4rem, 13vw, 3.25rem);
  font-weight: 800;
  letter-spacing: -0.055em;
  line-height: 0.86;
  /* Blanco, no tinta: el titular vive dentro del bloque de tinta. Se hereda del
     contenedor en vez de fijarse aquí para que el mismo estilo sirva si algún
     día esta cifra se usa sobre papel. */
  color: currentColor;
}
.ed-centavos {
  font-size: 0.36em;
  font-weight: 700;
  letter-spacing: -0.02em;
  /* Opacidad en vez de un gris fijo: sobre tinta, un gris calibrado para papel
     desaparece, y al revés también. Así el mismo estilo sirve en los dos sitios. */
  opacity: 0.55;
}

/* Versalitas: la voz pequeña de toda la portada —etiquetas, saludo, rótulos. */
.ed-versalita {
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  line-height: 1.4;
}
.ed-fechado {
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ink-faint);
}

/* Píldora de variación. Verde cuando se gasta menos; gris neutro cuando se
   gasta más — nunca roja: que suba el gasto un mes no es una emergencia, y
   pintarlo de alarma entrena a ignorar la alarma el día que sí importa. */
.ed-pildora {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border-radius: 999px;
  padding: 5px 11px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: -0.005em;
}
.ed-pildora--bien { background: var(--income-soft); color: var(--income-text); }
/* Sobre el bloque de tinta, el gris neutro del tema desaparece: la píldora se
   dibuja con un velo blanco translúcido, que funciona igual en los dos temas
   porque depende del bloque y no del lienzo. */
.ed-pildora--sobre-tinta {
  background: rgb(255 255 255 / 0.12);
  color: var(--on-navy-soft);
}

/* Filete de sección: la única frontera de la página. Sustituye a la tarjeta. */
.ed-seccion { border-top: 1px solid var(--regla); padding-top: 1.35rem; margin-top: 1.6rem; }

/* Filas de lista (las metas): filete entre renglones, ninguno al principio ni
   al final. La agenda ya no lo usa: sus renglones son tarjetas tintadas. */
.ed-filas > li + li { border-top: 1px solid var(--regla); }

/* Los avisos compartidos llegan como tarjetas y se dejan como caja: son
   interrupciones y tienen que leerse como tales. Arriba del todo llevan menos
   aire por debajo que una sección, para no empujar el saldo fuera del pliegue
   cuando aparecen los dos. */
.ed-avisos-arriba:not(:has(*)) { display: none; }
.ed-avisos-arriba { margin-top: 1rem; }

`


/** Sección de cuerpo: filete, rótulo numerado y contenido. */
function Seccion({
  numero,
  titulo,
  accion,
  children,
}: {
  numero?: string
  titulo?: string
  accion?: { texto: string; onClick: () => void }
  children: React.ReactNode
}) {
  return (
    <section className="ed-seccion">
      {titulo ? (
        <header className="mb-3.5 flex items-baseline gap-2.5">
          {numero ? <span className="ed-fechado shrink-0">{numero}</span> : null}
          <h2 className="min-w-0 flex-1 truncate font-display text-[20px] font-bold tracking-[-0.035em] text-ink">
            {titulo}
          </h2>
          {accion ? (
            <button
              type="button"
              onClick={() => {
                haptic('light')
                accion.onClick()
              }}
              className="-my-2.5 -mr-1 flex min-h-[44px] shrink-0 items-center px-1 text-[12px] font-bold uppercase tracking-[0.08em] text-primary-text active:opacity-60"
            >
              {accion.texto}
            </button>
          ) : null}
        </header>
      ) : null}
      {children}
    </section>
  )
}

/** De quién es el dinero que enseña el inicio: de todos, del fondo común o de uno. */
type Scope = 'combined' | 'joint' | 'personal'

/**
 * Alcance del inicio en versión editorial.
 *
 * Tres opciones dibujadas como pestañas de texto subrayadas: en una
 * composición sin cajas, una pastilla rellena sería el objeto más pesado de la
 * portada, por encima del propio titular. Sustituye a la pastilla deslizante
 * que había antes (`ScopeSwitcher`), que quedó sin uso y se borró.
 */
function Alcance({
  valor,
  onCambio,
  sinFondoComun,
  className,
}: {
  valor: Scope
  onCambio: (s: Scope) => void
  sinFondoComun: boolean
  className?: string
}) {
  const opciones: Array<{ value: Scope; label: string }> = [
    { value: 'combined', label: 'Conjunto' },
    { value: 'joint', label: 'Común' },
    { value: 'personal', label: 'Personal' },
  ].filter((o) => !(sinFondoComun && o.value === 'joint')) as Array<{ value: Scope; label: string }>

  return (
    <div
      role="tablist"
      aria-label="Alcance del resumen"
      className={cn('flex gap-5 border-b border-[var(--regla)]', className)}
    >
      {opciones.map((o) => {
        const activo = o.value === valor
        return (
          <button
            key={o.value}
            role="tab"
            type="button"
            aria-selected={activo}
            onClick={() => {
              if (!activo) {
                haptic('selection')
                onCambio(o.value)
              }
            }}
            className={cn(
              'relative min-h-[44px] pb-1 text-[13px] font-semibold transition-colors',
              activo ? 'text-ink' : 'text-ink-faint',
            )}
          >
            {o.label}
            {/* El subrayado es de 2 px y en tinta: sobre papel, un subrayado de
                color de marca competiría con la píldora y con el halo. */}
            <span
              className={cn(
                'absolute inset-x-0 -bottom-px h-0.5 rounded-full',
                activo ? 'bg-ink' : 'bg-transparent',
              )}
            />
          </button>
        )
      })}
    </div>
  )
}

function AccionPortada({
  Icon,
  label,
  tinte,
  onClick,
}: {
  Icon: typeof PlusIcon
  label: string
  tinte: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={() => {
        haptic('light')
        onClick()
      }}
      className="flex min-h-[44px] flex-col items-center gap-1 transition-transform duration-150 active:scale-95"
    >
      <span
        className={cn(
          // Altura fija en vez de `aspect-square`: cuadrado, el azulejo crecía
          // con el ancho de la pantalla y en 375 px pesaba más que el propio
          // saldo. 56 px deja el blanco táctil holgado y devuelve la
          // proporción a quien manda.
          'flex h-14 w-full items-center justify-center rounded-control',
          tinte,
        )}
      >
        <Icon size={19} weight="bold" />
      </span>
      {/* Vive dentro del bloque de tinta: con el gris del papel (`ink-soft`)
          daba 3.2:1 y las cuatro etiquetas se leían apagadas. */}
      <span className="ed-versalita text-on-navy-soft">{label}</span>
    </button>
  )
}
/**
 * El esqueleto copia la composición, no solo los huecos.
 *
 * Antes era papel claro de arriba abajo, con la dona antes que las acciones y
 * azulejos cuadrados: al llegar los datos, la mitad superior pasaba de golpe de
 * marfil a tinta y los botones cambiaban de forma. Es el parpadeo más visible
 * que puede tener la app, porque ocurre cada vez que se abre. Ahora el bloque de
 * tinta ya está ahí desde el primer fotograma y cada hueco mide lo que medirá
 * su pieza.
 */
function Cargando() {
  // Sobre la tinta, el gris del papel no se ve: los huecos van en velo blanco.
  const velo = 'bg-white/10'
  return (
    <Pagina>
      <BloqueTinta>
        <div className="flex min-h-[44px] items-center gap-3">
          <Skeleton className={cn('size-9 rounded-full', velo)} />
          <div className="flex flex-1 flex-col gap-1.5">
            <Skeleton className={cn('h-2.5 w-20', velo)} />
            <Skeleton className={cn('h-3.5 w-32', velo)} />
          </div>
        </div>
        <Skeleton className={cn('mt-4 h-6 w-44 rounded-pill', velo)} />
        <div className="pb-1 pt-5">
          <div className="flex h-7 items-center">
            <Skeleton className={cn('h-3 w-36', velo)} />
          </div>
          <Skeleton className={cn('mt-1.5 h-11 w-56', velo)} />
          <div className="mt-6 grid grid-cols-4 gap-2.5">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex flex-col items-center gap-1">
                <Skeleton className={cn('h-14 w-full rounded-control', velo)} />
                <div className="flex h-[15px] items-center">
                  <Skeleton className={cn('h-2.5 w-10', velo)} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </BloqueTinta>

      <section className="ed-seccion">
        <Skeleton className="h-5 w-48" />
        <Skeleton className="mx-auto mt-6 size-[216px] rounded-full" />
      </section>
    </Pagina>
  )
}

/* ── Utilidades ─────────────────────────────────────────────────────────── */

function saludo(): string {
  const hora = new Date().getHours()
  if (hora < 12) return 'Buenos días'
  if (hora < 19) return 'Buenas tardes'
  return 'Buenas noches'
}

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
const MESES_LARGOS = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
]
const DIAS = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb']

function dia(fecha: string): string {
  const [, mes, d] = fecha.split('-')
  return `${Number(d)} ${MESES[Number(mes) - 1]}`
}

/**
 * Línea de fechado, al estilo del pie de una cabecera de periódico.
 *
 * Se construye a mano en vez de con `toLocaleDateString` para no depender del
 * huso del navegador: las fechas del hogar llegan como `YYYY-MM-DD` y crear un
 * `Date` con ellas las corre un día en husos negativos.
 */
function fechado(hoy: string, desde: string, hasta: string): string {
  const [y, m, d] = hoy.split('-').map(Number) as [number, number, number]
  const semana = DIAS[new Date(y, m - 1, d).getDay()]!

  const [, mesDesde, diaDesde] = desde.split('-').map(Number) as [number, number, number]
  const [, mesHasta, diaHasta] = hasta.split('-').map(Number) as [number, number, number]

  // El mes se nombra una vez si el periodo cabe en uno, y dos si lo cruza.
  // Desde que el arranque del periodo es configurable, «periodo 25–24 ago» es
  // una lectura posible, y dice justo lo contrario de lo que pasa: el 25 es de
  // julio.
  const rango =
    mesDesde === mesHasta
      ? `${diaDesde}–${diaHasta} ${MESES[mesHasta - 1]}`
      : `${diaDesde} ${MESES[mesDesde - 1]} – ${diaHasta} ${MESES[mesHasta - 1]}`

  return `${semana} ${d} ${MESES[m - 1]} · periodo ${rango}`
}

function vencimiento(dias: number): string {
  if (dias < 0) return `Venció hace ${Math.abs(dias)} días`
  if (dias === 0) return 'Vence hoy'
  if (dias === 1) return 'Vence mañana'
  return `Vence en ${dias} días`
}

/**
 * Parte el monto en pesos y centavos.
 *
 * La expresión toma el separador decimal que haya puesto `Intl` en vez de
 * asumir el punto: el día que la app soporte otra moneda, el titular no se
 * parte por la mitad.
 */
function partirMonto(cents: number): { entero: string; centavos: string } {
  const texto = formatMoney(cents)
  const partes = /^(.*)([.,])(\d{2})$/.exec(texto)
  if (!partes) return { entero: texto, centavos: '' }
  return { entero: partes[1]!, centavos: `${partes[2]}${partes[3]}` }
}

/**
 * Variación del gasto contra el mes anterior.
 *
 * Es el único dato «nuevo» de la portada y sale de la serie que ya se pide con
 * `useCashflow(6)`: nada inventado. Se compara gasto contra gasto y se nombra
 * el mes, porque «12% menos» sin decir menos que cuándo no significa nada.
 */
function variacionDeGasto(
  flujo: CashflowResponse | undefined,
): { pct: number; baja: boolean; mes: string } | null {
  if (!flujo || flujo.months.length < 2) return null
  const i = flujo.months.findIndex((m) => m.month === flujo.currentMonth.month)
  const actual = i >= 0 ? flujo.months[i] : flujo.months[flujo.months.length - 1]
  const previo = i >= 0 ? flujo.months[i - 1] : flujo.months[flujo.months.length - 2]
  if (!actual || !previo || previo.expenseCents <= 0) return null

  const delta = (actual.expenseCents - previo.expenseCents) / previo.expenseCents
  const pct = Math.round(Math.abs(delta) * 100)
  // Por debajo del 1% no hay noticia: redondearía a 0% y la píldora mentiría.
  if (pct < 1) return null
  return { pct, baja: delta < 0, mes: MESES_LARGOS[Number(previo.month.split('-')[1]) - 1] ?? '' }
}

function vacio(): PoolBreakdown {
  return {
    liquidCents: 0,
    expectedIncomeCents: 0,
    incomingContributionsCents: 0,
    committedFixedCents: 0,
    committedCardsCents: 0,
    committedInstallmentsCents: 0,
    committedSavingsCents: 0,
    contributionPendingCents: 0,
    freeCents: 0,
    projectedFreeCents: 0,
    voucherCents: 0,
    savingsBalanceCents: 0,
    cardDebtCents: 0,
  }
}

/** Vista conjunta: el hogar entero leído como una sola bolsa. */
function combinar(availability: {
  joint: PoolBreakdown | null
  perMember: Record<string, PoolBreakdown>
}): PoolBreakdown {
  const pools = [
    ...(availability.joint ? [availability.joint] : []),
    ...Object.values(availability.perMember),
  ]
  const sum = (pick: (p: PoolBreakdown) => number): number =>
    pools.reduce((total, p) => total + pick(p), 0)

  return {
    liquidCents: sum((p) => p.liquidCents),
    expectedIncomeCents: sum((p) => p.expectedIncomeCents),
    // Las aportaciones pendientes se anulan al mirar el hogar completo: son un
    // movimiento entre bolsillos internos, no dinero que entre ni salga.
    incomingContributionsCents: 0,
    committedFixedCents: sum((p) => p.committedFixedCents),
    committedCardsCents: sum((p) => p.committedCardsCents),
    committedInstallmentsCents: sum((p) => p.committedInstallmentsCents),
    committedSavingsCents: sum((p) => p.committedSavingsCents),
    contributionPendingCents: 0,
    freeCents: sum((p) => p.freeCents),
    projectedFreeCents: sum((p) => p.projectedFreeCents),
    voucherCents: sum((p) => p.voucherCents),
    savingsBalanceCents: sum((p) => p.savingsBalanceCents),
    cardDebtCents: sum((p) => p.cardDebtCents),
  }
}
